import React, { useRef, useState } from 'react';
import { Button } from '@view/components';
import GridPanel from './GridPanel';
import { cn } from '@utils';
import { IViewSettings } from '@data/models';
import { ConfigProvider } from '@data/providers';
import { ISceneData } from '@data/providers/SceneDataProvider';
import { useCompanyColorScheme } from 'src/hooks/useCompanyColorScheme';
import styles from './styles.scss';

export type GridOption = {
  value: number | null;
  label: string;
};

interface GridSwitcherProps {
  viewSettings: IViewSettings;
  setViewSetting: (setting: keyof IViewSettings, value: any) => void;
  companyColorScheme?: ReturnType<typeof useCompanyColorScheme>[0];
  configProvider: ConfigProvider;
  sceneData?: ISceneData;
}

const GridSwitcher = ({
  viewSettings,
  setViewSetting,
  companyColorScheme,
  configProvider,
}: GridSwitcherProps) => {
  const [gridPanelOpen, setGridPanelState] = useState<boolean>(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const gridOptions: GridOption[] = [{ value: null, label: 'Grid off' }];

  const gridFeatureConfig = configProvider.getFeatureConfig<any>('grid');

  if (gridFeatureConfig?.enabled) {
    gridFeatureConfig.sizes.forEach((size: number) => {
      gridOptions.push({ value: size, label: `${size} mm` });
    });
  }

  const handleGridSwitcherMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setGridPanelState(true);
  };

  const handleGridSwitcherMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setGridPanelState(false);
    }, 600);
  };

  const handleGridPanelClick = (gridSize: number | null) => {
    if (gridSize) {
      setViewSetting('grid', true);
      setViewSetting('gridSize', gridSize);
    } else {
      setViewSetting('grid', false);
    }
  };

  const handleGridButtonClick = () => {
    const currentSelectedOption = !viewSettings.grid
      ? null
      : viewSettings.gridSize;
    const options = gridOptions.map((v) => v.value);
    const index = options.indexOf(currentSelectedOption);
    const newOption = options[(index + 1) % options.length];
    handleGridPanelClick(newOption);
  };

  return (
    <div className="relative">
      <Button
        active={viewSettings.grid || gridPanelOpen}
        className={cn(styles.viewSettingsBar_button, 'min-h-full')}
        onMouseEnter={handleGridSwitcherMouseEnter}
        onMouseLeave={handleGridSwitcherMouseLeave}
        onClick={handleGridButtonClick}
      >
        <img
          src="/assets/images/grid_icon.svg"
          className={cn(
            companyColorScheme?.light && 'invert-[.75] drop-shadow-lg'
          )}
          alt="View Settings"
        />
      </Button>
      {gridPanelOpen && (
        <GridPanel
          onClose={() => {
            setGridPanelState(false);
          }}
          onMouseEnter={handleGridSwitcherMouseEnter}
          onMouseLeave={handleGridSwitcherMouseLeave}
          gridState={viewSettings.grid}
          gridSize={viewSettings.gridSize}
          gridOptions={gridOptions}
          onGridPanelClick={handleGridPanelClick}
        />
      )}
    </div>
  );
};

export default GridSwitcher; 